import {
  DEFAULT_FILL_COLOR,
  MINUTE_IN_MILLISECONDS,
  PLAYBACK_FRAMES_PER_SECOND,
  SECOND_IN_MILLISECONDS,
} from '../../constants';
import MediaSegmentEdit from '../../model/media/MediaSegmentEdit';
import MediaSegmentView from '../../model/media/MediaSegmentView';
import { MEDIA_SEGMENT_EDIT_TYPE } from '../../model/media/constants';
import { floorForPrecision } from '../../util/numbers';

export const BACKGROUND_UPDATE_RECORDING_GROUP =
  'BACKGROUND_UPDATE_RECORDING_GROUP';
export const DEBUG_SHOULD_AUTO_PLAY = false; // Auto play on new episode load

export const FIXED_PRECISION_FRAME_DURATION = floorForPrecision(
  1 / PLAYBACK_FRAMES_PER_SECOND,
  11,
);
export const CONTENT_TIME_PRECISION = 1e-10;
export const MIN_SOURCE_MEDIA_RATIO_FOR_LAYOUTS = 9 / 16;
export const MAX_SCALE_MULTIPLIER = 3;
export const SCRUB_SENSITIVITY_MULTIPLIER = 2;

export const REDUX_NAMESPACE = 'VIDEO_REFORMATTING';
export const SOURCE_MEDIA_SYNC_TIMEOUT_MS = 20 * MINUTE_IN_MILLISECONDS;
export const SHOT_POLLING_INTERVAL_MS = 1 * SECOND_IN_MILLISECONDS;
export const SHOT_LIST_THUMBNAIL_SIZE = 90;
export const MAX_SHOT_POLL_CONSECUTIVE_RETRIES = 5;

export const SHOT_ELEMENT_TYPES = {
  BACKGROUND: 'BACKGROUND',
  GROUP: 'GROUP',
  MASK: 'MASK',
  SOURCE: 'SOURCE',
  VIEW: 'VIEW',
};

export const SOURCE_ELEMENT_TYPES = {
  UPLOADED: 'UPLOADED',
  USER_CREATED: 'USER_CREATED',
};

export const SHOT_BACKGROUND_TYPES = {
  BLUR: 'BLUR',
  FILL: 'FILL',
};

export const VALID_SHOT_BACKGROUND_TYPES = new Set(
  Object.keys(SHOT_BACKGROUND_TYPES),
);

const genericMSV = new MediaSegmentView();
const letterboxCropMSE = new MediaSegmentEdit(
  MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX,
);
letterboxCropMSE.views = [genericMSV];
const split2CropMSE = new MediaSegmentEdit(
  MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2,
);
split2CropMSE.views = [genericMSV, genericMSV];
const split3CropMSE = new MediaSegmentEdit(
  MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3,
);
split3CropMSE.views = [genericMSV, genericMSV, genericMSV];
const staticCropMSE = new MediaSegmentEdit(MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP);
staticCropMSE.views = [genericMSV];

export const DEFAULT_MEDIA_SEGMENT_EDITS = {
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: letterboxCropMSE,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: split2CropMSE,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: split3CropMSE,
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: staticCropMSE,
};
export const DEFAULT_MEDIA_SEGMENT_EDIT =
  DEFAULT_MEDIA_SEGMENT_EDITS[MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP];
export const SHOT_DIVIDER_HEIGHT = 15;
export const SHOT_VIEW_HEIGHT_FIT_WIDTH = -1;
export const SINGLE_VIEW_FULL_HEIGHT_RATIO = [1.0]; // one screen, 100% height

// todo(chuti): ALMOST_A_THIRD_SPLIT should be dynamic?
export const ALMOST_A_THIRD_SPLIT = 686; // Intentionally hard coded to this value (@lisa-chen)
export const FOUR_FIVE_RATIO = 4 / 5;

export const SHOT_LAYOUT_SIZES = {
  CUSTOM: 'CUSTOM',
  RATIO_1_1: 'RATIO_1_1',
  RATIO_1_2: 'RATIO_1_2',
  RATIO_2_1: 'RATIO_2_1',
  SQUARE: 'SQUARE',
  TALL: 'TALL',
  WIDE: 'WIDE',
};

export const MEDIA_SEGMENT_EDIT_LAYOUTS = {
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: {
    CUSTOM: SHOT_LAYOUT_SIZES.CUSTOM,
    SQUARE: SHOT_LAYOUT_SIZES.SQUARE,
    TALL: SHOT_LAYOUT_SIZES.TALL,
    WIDE: SHOT_LAYOUT_SIZES.WIDE,
  },
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: {
    CUSTOM: SHOT_LAYOUT_SIZES.CUSTOM,
    RATIO_1_1: SHOT_LAYOUT_SIZES.RATIO_1_1,
    RATIO_1_2: SHOT_LAYOUT_SIZES.RATIO_1_2,
    RATIO_2_1: SHOT_LAYOUT_SIZES.RATIO_2_1,
  },
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: {
    CUSTOM: SHOT_LAYOUT_SIZES.CUSTOM,
  },
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: {
    CUSTOM: SHOT_LAYOUT_SIZES.CUSTOM,
  },
};

export const MEDIA_SEGMENT_EDIT_DEFAULT_LAYOUTS = {
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]:
    MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.WIDE,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]:
    MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_2_1,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]:
    MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_3.CUSTOM,
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]:
    MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_3.CUSTOM,
};

export const MEDIA_SEGMENT_EDIT_LAYOUT_DEFAULT_BACKGROUND_TYPE = {
  // [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: SHOT_BACKGROUND_TYPES.BLUR,
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: SHOT_BACKGROUND_TYPES.FILL,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: SHOT_BACKGROUND_TYPES.FILL,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: SHOT_BACKGROUND_TYPES.FILL,
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: null,
};

export const MEDIA_SEGMENT_EDIT_LAYOUT_DEFAULT_BACKGROUND_VALUE = {
  // [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: null,
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: DEFAULT_FILL_COLOR,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: DEFAULT_FILL_COLOR,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: DEFAULT_FILL_COLOR,
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: null,
};

export const MEDIA_SEGMENT_EDIT_LAYOUT_BACKGROUND_TYPES = {
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: [
    SHOT_BACKGROUND_TYPES.BLUR,
    SHOT_BACKGROUND_TYPES.FILL,
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: [SHOT_BACKGROUND_TYPES.FILL],
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: [SHOT_BACKGROUND_TYPES.FILL],
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: [],
};

export const DEFAULT_BLUR_FILTER_VALUE_PX = 60;

export const MEDIA_SEGMENT_EDIT_LAYOUT_SUPPORTS_DIRECT_MANIUPULATION = {
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: false,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: true,
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: false,
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: false,
};

export const CHAPTER_LENGTH_ABSOLUTE_MIN_SECOND =
  1 / PLAYBACK_FRAMES_PER_SECOND;
export const CHAPTER_LENGTH_MIN_SECOND = 2;
export const DEFAULT_CHAPTER_LENGTH_TARGET_SECOND = 10;

// chapter breaks max length
export const CHAPTER_LENGTH_MAX_SECOND = 20;
export const CHAPTER_LENGTH_MAX_SECOND_FOR_PUBLISHER = 10;
export const CHAPTER_LENGTH_MAX_SECOND_FOR_SHOW_PUBLISHER = 20;

// Number of chapters (that equates to a Snap) for Happening Now
export const MAX_CHAPTERS_FOR_HAPPENING_NOW = 1;

export const CHAPTER_WORKER_MSG = 'CHAPTER_WORKER_MSG';

export const DEFAULT_SHOT_LIST_LOGGING_TIMER_INTERVAL_MINS = 10;

export const SHOT_TRANSFORMATION_TYPE = {
  PAN: 'PAN',
  STATIC: 'STATIC',
};

export const MAXIMUM_LETTERBOX_THRESHOLD = 0.5; /* CONTENT-1037: No more than 50% of shots should have letterbox type */

export const EDITOR_LIST_ITEM_DEFAULT_HEIGHT = 83;

export const LEFT_PANEL_TABS = {
  CLIPS: 'CLIPS',
  SHOTS: 'SHOTS',
};

export const SHOT_LIST_MENU_ICONS = {
  CREATE_CLIP: 'image-gallery',
  SKIP_SHOTS: 'check',
};
